import { useTheme } from '@zhiyue/core';
import type { IntelligentWritingMarkdownEditorProps } from '@zhiyue/intelligent-writing';
import { Alert, Button, message, Spin, Upload } from 'antd';
import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import '@zhiyue/intelligent-writing/style.css';
import { FOLLOW_UP_CONTENT_LIMIT, safeContentUrl } from './content';
import {
  mapMarkdownImages,
  markdownFileImages,
  restoreFileReferences,
} from './file-references';
import { loadMarkdownFile, uploadMarkdownFile } from './files';
import styles from './style.module.less';
import { loadWritingEditor } from './writing-editor';

export type MarkdownEditorHandle = { getMarkdown: () => string };
export const MarkdownEditor = React.forwardRef<
  MarkdownEditorHandle,
  {
    value?: string;
    onChange?: (value: string) => void;
    bizType?: string;
    bizUuid?: string;
    onBusyChange?: (busy: boolean) => void;
  }
>(function MarkdownEditor(
  { value = '', onChange, bizType, bizUuid, onBusyChange },
  ref,
) {
  const { navTheme } = useTheme();
  const [Editor, setEditor] =
    useState<React.ComponentType<IntelligentWritingMarkdownEditorProps>>();
  const [error, setError] = useState('');
  const [displayValue, setDisplayValue] = useState(value);
  const latest = useRef(value);
  const props = useRef({ onChange, bizType, bizUuid, onBusyChange });
  props.current = { onChange, bizType, bizUuid, onBusyChange };
  const urls = useRef(new Map<string, string>());
  const pendingUrls = useRef(new Map<string, Promise<string>>());
  const alive = useRef(true);
  const jobs = useRef(0);
  const loaded = useRef(false);
  const busy = () =>
    props.current.onBusyChange?.(!loaded.current || jobs.current > 0);
  useImperativeHandle(ref, () => ({ getMarkdown: () => latest.current }), []);
  const emit = (markdown: string) => {
    const canonical = restoreFileReferences(markdown, urls.current);
    latest.current = canonical;
    setDisplayValue(markdown);
    props.current.onChange?.(canonical);
  };
  const displayUrl = async (stable: string) => {
    if (urls.current.has(stable)) return urls.current.get(stable)!;
    if (!pendingUrls.current.has(stable))
      pendingUrls.current.set(
        stable,
        (async () => {
          const display = await loadMarkdownFile(stable);
          if (!alive.current) {
            if (display.startsWith('blob:')) URL.revokeObjectURL(display);
            return stable;
          }
          urls.current.set(stable, display);
          return display;
        })().finally(() => pendingUrls.current.delete(stable)),
      );
    return pendingUrls.current.get(stable)!;
  };
  const upload = async (file: File) => {
    jobs.current++;
    busy();
    try {
      const stable = await uploadMarkdownFile(
        file,
        props.current.bizType,
        props.current.bizUuid,
      );
      return await displayUrl(stable);
    } catch (failure) {
      message.error(failure instanceof Error ? failure.message : '上传失败');
      throw failure;
    } finally {
      jobs.current--;
      if (alive.current) busy();
    }
  };
  useEffect(() => {
    alive.current = true;
    busy();
    void loadWritingEditor()
      .then((module) => {
        if (!alive.current) return;
        loaded.current = true;
        setEditor(() => module.IntelligentWritingMarkdownEditor);
        busy();
      })
      .catch(() => {
        if (alive.current) setError('编辑器加载失败，请关闭后重试');
      });
    return () => {
      alive.current = false;
      for (const url of urls.current.values())
        if (url.startsWith('blob:')) URL.revokeObjectURL(url);
      urls.current.clear();
    };
  }, []);
  useEffect(() => {
    let active = true;
    latest.current = value;
    const paths = markdownFileImages(value);
    setDisplayValue(
      mapMarkdownImages(value, (url) => urls.current.get(url) || url),
    );
    if (paths.length) {
      jobs.current++;
      busy();
      void Promise.all(paths.map(displayUrl))
        .then(() => {
          if (active)
            setDisplayValue(
              mapMarkdownImages(
                latest.current,
                (url) => urls.current.get(url) || url,
              ),
            );
        })
        .catch(() => {
          if (active) message.error('图片读取失败，请检查文件权限后重试');
        })
        .finally(() => {
          jobs.current--;
          if (alive.current) busy();
        });
    }
    return () => {
      active = false;
    };
  }, [value]);
  return (
    <div
      className={styles.editor}
      data-theme={navTheme === 'realDark' ? 'dark' : 'light'}
      onClickCapture={(event) => {
        const link =
          event.target instanceof Element ? event.target.closest('a') : null;
        if (link && !safeContentUrl(link.getAttribute('href') || ''))
          event.preventDefault();
      }}
    >
      {error ? (
        <Alert type="error" title={error} />
      ) : Editor ? (
        <Editor
          value={displayValue}
          onChange={emit}
          locale="zh-CN"
          layout="plain"
          sectionNumberingEnabled={false}
          imageUploadHandler={upload}
          height={320}
        />
      ) : (
        <Spin size="small" />
      )}
      <div className={styles.footer}>
        <Upload
          showUploadList={false}
          disabled={!Editor || !bizType || !bizUuid}
          beforeUpload={(file) => {
            void upload(file)
              .then((display) => {
                if (!alive.current) return;
                const stable = restoreFileReferences(display, urls.current);
                const label = file.name.replace(/[\\[\]]/g, '\\$&');
                emit(`${latest.current}\n\n[${label}](${stable})`);
              })
              .catch(() => {});
            return false;
          }}
        >
          <Button size="small" disabled={!Editor || !bizType || !bizUuid}>
            添加附件
          </Button>
        </Upload>
        <span role="status">
          {value.length.toLocaleString()} /{' '}
          {FOLLOW_UP_CONTENT_LIMIT.toLocaleString()}
        </span>
      </div>
    </div>
  );
});
